import React, { useCallback, useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { Accordion, Button, Form, Modal, Spinner } from 'react-bootstrap'
import CodeMirror from '@uiw/react-codemirror'
import { html } from '@codemirror/lang-html'
import basicSetupConfig from './CodeMirrorConfig'
import { useHistory } from 'react-router-dom'

import { ErrorBox } from '../ErrorBox/ErrorBox'
import { ACTION_CREATE } from '../CreateUpdateSiteModal/interface'
import CustomModal from 'components/CustomModal/CustomModal'

import './CodeEditor.css'

const CodeEditor = ({ show, onHide, onSiteAction, setDraggedFile, disableRouting = false }) => {
  const [code, setCode] = useState('')
  const [isDirty, setIsDirty] = useState(false)
  const [isPublishing, setIsPublishing] = useState(false)
  const [errorPublishing, setErrorPublishing] = useState(false)
  const [hasErrored, setHasErrored] = useState(false)
  const [pageTitle, setPageTitle] = useState('')
  const [headSnippet, setHeadSnippet] = useState('')
  const history = useHistory()

  const onChange = useCallback((val, viewUpdate) => {
    if (!isDirty) {
      setIsDirty(true)
    }
    setCode(val)
  }, [])

  const onChangeHeader = useCallback((val, viewUpdate) => {
    setHeadSnippet(val)
  }, [])

  useEffect(() => {
    if (show && !disableRouting) {
      history.push({ pathname: '/manage/upload/text' })
    }
  }, [show, history, disableRouting])

  const clearEditor = () => {
    setCode('')
    setHeadSnippet('')
    setIsDirty(false)
    setIsPublishing(false)
    setErrorPublishing(false)
    setHasErrored(false)
  }

  const onPublish = () => {
    setErrorPublishing(false)
    setIsPublishing(true)
    const htmlContent = generateHTML(pageTitle, headSnippet, code)
    if (code) {
      const newFile = new File([htmlContent], 'code.html', {
        type: 'text/html',
        lastModified: new Date(),
        path: 'code.html'
      })
      setDraggedFile(newFile)
      onSiteAction(ACTION_CREATE)
    }
    clearEditor()
    onHide()
    setIsPublishing(false)
  }

  const generateHTML = (pageTitle, headSnippet, code) => `<!DOCTYPE html>
        <html>
          <head>
            <title>${pageTitle}</title>
            ${headSnippet}
          </head>
          <body>
            ${code}
          </body>
        </html>`

  const onDiscard = () => {
    clearEditor()
    if (!disableRouting) {
      history.push({ pathname: '/manage' })
    }
    onHide()
  }

  return (
    <CustomModal
      className="p-5"
      backdrop="static"
      size="lg"
      show={show}
      aria-labelledby="contained-modal-title-vcenter"
      centered
      enforceFocus={false}
      mobileCloseButton={false}
    >
      <div className="p-4">
        <>
          <div className="mb-2">
            <h2 className="bold color-purple-2 font-22">Paste HTML</h2>
            <div className="grey">Paste or write HTML below to publish it as a link.</div>
          </div>
          <div className="code-editor-container">
            <CodeMirror
              value={code}
              height="auto"
              extensions={[html()]}
              onChange={onChange}
              placeholder="Paste your HTML code here…"
              basicSetup={basicSetupConfig}
              autoFocus
            />
          </div>
          <Accordion className="mt-3">
            <Accordion.Item eventKey="0">
              <Accordion.Header variant="link small">Advanced options</Accordion.Header>
              <Accordion.Body>
                <Form.Group className="mb-3 mt-3">
                  <Form.Control
                    type="text"
                    placeholder="Page title"
                    value={pageTitle}
                    onChange={(e) => setPageTitle(e.target.value)}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Head HTML <span className="font-small">(e.g. analytic tags & scripts)</span>
                  </Form.Label>
                  <CodeMirror
                    value={headSnippet}
                    theme="light"
                    height="auto"
                    extensions={[html()]}
                    onChange={onChangeHeader}
                    placeholder="Enter HTML for the <head> section"
                    basicSetup={basicSetupConfig}
                  />
                </Form.Group>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
          <div className="mt-3">
            {isDirty ? (
              <>
                {errorPublishing && <div style={{ color: 'red' }}>Unable to publish, please try again.</div>}
                <div>
                  <Button className="ms-2 float-right" onClick={onPublish} disabled={isPublishing}>
                    Continue {isPublishing && <Spinner animation="border" variant="light" size="sm" />}
                  </Button>
                  <Button className="float-right" variant="outline-danger" onClick={onDiscard} disabled={isPublishing}>
                    Discard
                  </Button>
                </div>
              </>
            ) : (
              <Button className="float-right" variant="outline-primary" onClick={onDiscard}>
                Close
              </Button>
            )}
          </div>
        </>
        {hasErrored && <ErrorBox onClick={onHide} />}
      </div>
    </CustomModal>
  )
}

export default connect(null, (dispatch) => ({ dispatch }))(CodeEditor)
